<template>
  <PortalWidget :widget="widget">
    <div class="reporting-call-logs-header">
      <h2 v-t="'RTT_DASHBOARD_FILTER_HEADER'" class="reporting-call-logs-header__title" />
    </div>
    <div class="r-form rtb-dashboard display-flex flex--wrap p-b-30">
      <SearchableDropdown
        v-model="timeZoneId"
        rules="required"
        :config="timeZoneOptions"
        class="rtb-export-timezone m-r-15 m-b-15"
        :clearable="false"
        disabled
      />
      <DateRangePicker
        date-range-button-class="btn daterangepicker-btn--form m-r-15 m-b-15"
        class="m-b-15"
        :date-range="timeRange"
        :options="dateRangeConfig"
        :time-zone-id="translatedTimeZoneId"
        @onDateRangeChange="onDateRangeChange"
      />
      <PrimaryButton
        :show-loader-on-form="isSearching"
        type="button"
        class="p-l-15 m-b-15"
        :class="isSearching ? 'p-r-35' : 'p-r-15'"
        @click="onFilter(timeRange)"
      >
        <i class="fa fa-filter" aria-hidden="true" />
        {{ $t('RTT_DASHBOARD_FILTER') }}
      </PrimaryButton>
    </div>
    <RingTreeTargetPings
      :ping-data="rttPingData || []"
      :date-range="currentFilteredTimeRangeDetails"
      :is-loading="isSearching"
      class="rtb-stats__widget--alt m-b-20"
    />
    <RingTreeTargetErrors
      :error-data="rttErrorData || []"
      :date-range="currentFilteredTimeRangeDetails"
      :is-loading="isSearching"
      class="rtb-stats__widget--alt m-b-20"
    />
  </PortalWidget>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import 'moment-timezone';
import messages from '../../locales';
import sharedMessages from '@/core/locales';
import { dateTimeService, utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { cloneDeep } from 'lodash'
import { STATUS_PENDING, STATUS_ERROR } from '@/store/helpers';

import RingTreeTargetPings from '@/modules/ringTreeTargets/components/RingTreeTargetPings'
import RingTreeTargetErrors from '@/modules/ringTreeTargets/components/RingTreeTargetErrors'
import PrimaryButton from '@/core/components/PrimaryButton';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import DateRangePicker from '@/core/components/DateRangePicker/DateRangePicker';
import PortalWidget from '@/core/components/PortalWidget.vue';

/**
 * @type {Record<"errorData" | "pingData",
 * [import('@ag-grid-community/core').ColDef
 *  & {field: (data.structures.RttDashboardPingData | data.structures.RttDashboardErrorData) }]>}
 */
const timeZoneConfig = {
  reduce: (option) => option.id,
  label: 'display',
};

export default {
  name: 'RingTreeTargetDashboard',
  components: {
    PrimaryButton,
    SearchableDropdown,
    DateRangePicker,
    RingTreeTargetPings,
    RingTreeTargetErrors,
    PortalWidget
  },
  data() {
    return {
      timeRange: [],
      timeZoneId: '',
      isExporting: false,
      logData:  [],
      rttPingData: [],
      rttErrorData: [],
      firstLoad: true,
      currentFilteredTimeRangeDetails: [],
      widget: {
        title: this.$i18n.t('RING_TREE_DASHBOARD'),
        iconClass: 'fa fa-dot-circle-o'
      }
    }
  },
  computed: {
    ...mapState(['timezones']),
    ...mapGetters(['accountTimezoneId']),
    ...mapGetters('ringTreeTargets', ['getRttDashLogStatus']),
    translatedTimeZoneId() {
      return dateTimeService.getTimeZoneMapping(this.timeZoneId);
    },
    dateRangeConfig() {
      return {
        opens: 'right',
        timePicker: false,
        autoApply: true,
        minDate: this.minStartDate,
        maxDate: this.maxEndDate,
        locale: {
          format: 'M/DD/YYYY'
        },
      };
    },
    timeZoneOptions() {
      return {
        options: this.timezones,
        ...timeZoneConfig,
        placeholder: 'Select Timezone'
      };
    },
    isSearching() {
      return this.getRttDashLogStatus.status === STATUS_PENDING || this.firstLoad;
    },
  },
  created() {
    if (!this.timezones?.length) {
      this.getTimezones();
    }
    this.timeZoneId = this.accountTimezoneId;
    this.timeRange = [moment().startOf('day'), moment().endOf('day')];
    this.onDateRangeChange({ timeRange: this.timeRange });
    this.onFilter(this.timeRange);
    this.minStartDate = this.setMinStartDate();
    this.maxEndDate = moment().endOf('day').tz(this.translatedTimeZoneId);
  },
  methods: {
    ...mapActions(['getTimezones']),
    ...mapActions('ringTreeTargets', ['getRttDashLog']),
    onDateRangeChange(dateRangeDetails) {
      this.timeRange = [moment(dateRangeDetails.timeRange[0]._d), moment(dateRangeDetails.timeRange[1]._d)]
    },
    setMinStartDate() {
      const minStart = moment().startOf('day').subtract(6, 'months').tz(this.translatedTimeZoneId);
      const minStartData = moment('2023-10-12 00:00:00').tz(this.translatedTimeZoneId);
      if (minStartData.isAfter(minStart)) {
        return minStartData.tz(this.translatedTimeZoneId);
      }
      return minStart.tz(this.translatedTimeZoneId);
    },
    async onFilter(timeRangeDetails) {
      const start = moment(timeRangeDetails[0]._d).format('MM-DD-YYYY');
      const end = moment(timeRangeDetails[1]._d).format('MM-DD-YYYY');


      const dates = [start, end];

      this.currentFilteredTimeRangeDetails = dates;

      const logs = await this.getRttDashLog(dates);

      if (this.getRttDashLogStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.getRttDashLogStatus.error)
        this.firstLoad = false;
        return
      }

      // need to deep copy here
      const rttPings = cloneDeep(logs.values);
      const rttErrors = cloneDeep(logs.values);

      this.rttPingData = rttPings.map(item => {
        item.values = item.values.filter(column => {
          return ['winsPing', 'acceptedPing', 'rejectedCount'].includes(column.messageName);
        })
        return item;
      })

      this.rttErrorData = rttErrors.map(item => {
        item.values = item.values.filter(column => {
          return !['winsPing', 'acceptedPing', 'rejectedCount'].includes(column.messageName);
        })
        return item;
      })

      this.rttPingData = this.rttPingData.map(item => {
        const returnObj = {
          'rttId': item.rttId,
          'rttName': item.rttName,
        }

        item.values.forEach(value => {
          returnObj[value.messageName] = value.total;
        })

        return returnObj;
      })

      this.rttErrorData = this.rttErrorData.map(item => {
        const returnObj = {
          'rttId': item.rttId,
          'rttName': item.rttName,
        }

        item.values.forEach(value => {
          returnObj[value.messageName] = value.total;
        })

        this.firstLoad = false;
        return returnObj;
      })
    }
  },

  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>
